import React, { useEffect, useMemo, useRef, useState } from 'react';
import { activityRow, activityHeader, activityIconSlot, shimmerText } from './activityShimmer';
import styled, { css } from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ContentItem,
  ToolActionContentItem,
  CodeExecutionContentItem,
  SearchResultsContentItem,
} from '../../shared/interfaces';
import ToolActionBlock from './ToolActionBlock';
import CodeExecutionBlock from './CodeExecutionBlock';
import SearchResultsBlock from './SearchResultsBlock';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../../shared/Constants';
import { buildActivityText, shouldSummarize } from '../../utils/activitySummary';
import { runSignature, getThinkingScope } from '../../utils/thinkingGroup';
import { preloadFavicon } from '../../utils/faviconPreload';
import { useStableDisclosure } from '../../utils/stableDisclosure';

// Expanded/collapsed state per run signature. Module-level so it survives the
// remount at the end of generation (the streaming subtree is swapped for the
// persisted-message subtree, which resets local useState). Session-scoped.
const expandedRuns = new Map<string, boolean>();
// Last sabiazinho summary per run signature: the label KEEPS the summary after
// the group finishes (never falls back to a generic "Pensou" mid-conversation);
// same remount-survival mechanism as expandedRuns.
const summaryBySig = new Map<string, string>();

interface Props {
  /** Stable identity for persisting expansion/summary across remounts —
      message index + run ordinal ("m3-r0"). Content-derived signatures break
      when the reasoning draft is REPLACED by its finalized text (the head
      changes), losing the sabiazinho label mid-turn; a positional key never
      drifts. Falls back to runSignature(items) when absent. */
  persistKey?: string;
  /** The pre-answer burst, in stream order (see partitionThinkingBurst). */
  items: ContentItem[];
  /** True while the model is still thinking / running the pre-answer tools. */
  pending: boolean;
  /** The user stopped the generation while this group was the live activity:
      label it as interrupted instead of looking like a completed turn. */
  interrupted?: boolean;
}

/**
 * One collapsed, expandable "Pensando… / Pensou" line that aggregates the
 * whole pre-answer phase: the reasoning draft (streaming token-by-token) plus
 * every tool/code step that ran before the first visible answer text. Keeps a
 * turn with 3-4 pre-answer calls to a single line instead of a pile of boxes.
 */
function ThinkingGroup({ items, pending, interrupted, persistKey }: Props) {
  // Chat-scoped positional key: "chatId|m<idx>-r<ordinal>". Without the chat
  // prefix a new chat's first group inherited the previous chat's label.
  const sig = persistKey ? `${getThinkingScope()}|${persistKey}` : runSignature(items);
  const [open, setOpen] = useState<boolean>(() => expandedRuns.get(sig) ?? false);
  const { disclosureRef, toggleDisclosure } = useStableDisclosure(open, setOpen);

  // Write-through on every change: the signature can drift in the very first
  // moments of a stream (first item still filling); re-storing under the
  // current sig keeps the final mount able to restore the last state.
  useEffect(() => {
    if (expandedRuns.size > 500) expandedRuns.clear();
    if (sig) expandedRuns.set(sig, open);
  }, [sig, open]);


  // Pre-warm citation favicons: the search results land here seconds before
  // the model writes the citations, so the chips mount with the icon cached.
  useEffect(() => {
    for (const it of items as any[]) {
      if (it && it.type === 'search_results') {
        for (const r of it.results || []) preloadFavicon(r.url || '');
      }
    }
  }, [items]);

  // Live label: "Pensando" until enough activity accumulates, then a <=8-word
  // sabiazinho-4 summary of what is happening, refreshed as activity grows.
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [summary, setSummary] = useState<string>(() => (sig && summaryBySig.get(sig)) || '');
  // Write-through so the summary survives the stream->final subtree swap
  // (and signature drift while the first item is still filling).
  useEffect(() => {
    if (summaryBySig.size > 500) summaryBySig.clear();
    if (sig && summary) summaryBySig.set(sig, summary);
  }, [sig, summary]);
  const inFlightRef = useRef(false);
  const lastLenRef = useRef<number | null>(null);
  const lastCallAtRef = useRef(0);
  const disabledRef = useRef(false);
  const activityText = useMemo(() => buildActivityText(items as any[]), [items]);

  useEffect(() => {
    if (!pending || !isAuthenticated || disabledRef.current || inFlightRef.current) return;
    const len = activityText.length;
    if (!shouldSummarize(len, lastLenRef.current, Date.now() - lastCallAtRef.current)) return;
    inFlightRef.current = true;
    lastLenRef.current = len;
    lastCallAtRef.current = Date.now();
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await fetch(`${API_BASE}/api/chat/activity_summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Auth ${token}` },
          body: JSON.stringify({ text: activityText }),
        });
        if (resp.status === 404) { disabledRef.current = true; return; } // feature off
        if (!resp.ok) return;
        const data = await resp.json();
        if (data && data.summary) setSummary(data.summary);
      } catch {
        disabledRef.current = true; // auth/network problem: stop trying this turn
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [activityText, pending, isAuthenticated, getAccessTokenSilently]);

  const reasoningText = items
    .filter((it: any) => it.type === 'reasoning')
    .map((it: any) => (it.reasoning || '') as string)
    .filter((t) => t.trim())
    .join('\n\n');
  const steps = items.filter(
    (it: any) =>
      it.type === 'tool_action' ||
      it.type === 'code_execution' ||
      it.type === 'search_results', // web_search: o resultado E o passo
  );
  if (!pending && !reasoningText && steps.length === 0) return null;

  return (
    <Row ref={disclosureRef} data-download-exclude>
      <Header role="button" aria-expanded={open} onClick={toggleDisclosure}>
        {/* Tudo roda dentro do grupo recolhido: o anel e o shimmer dizem que
            ainda está trabalhando, como no ExtractingFilesBlock (mesmo slot de 15px). */}
        <IconWrap>
          {pending ? (
            <CircularProgress data-testid="thinking-progress" size={13} thickness={5}
              sx={{ color: 'text.secondary' }} />
          ) : (
            <PsychologyIcon data-testid="thinking-icon" style={{ fontSize: 15 }} />
          )}
        </IconWrap>
        <Verb $pending={pending} data-preserve-full-label={pending && !summary}>
          {/* Sticky: once sabiazinho labeled the activity, keep that label —
              a finished group never regresses to a generic "Pensou". */}
          {pending
            ? (summary || 'Pensando')
            : interrupted
              ? (summary ? `${summary} · interrompido` : 'Pensou · interrompido')
              : (summary || 'Pensou')}
        </Verb>
        {steps.length > 0 && <Counter>{steps.length}</Counter>}
        <ChevronWrap>
          {open ? (
            <ExpandLessIcon style={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon style={{ fontSize: 16 }} />
          )}
        </ChevronWrap>
      </Header>
      {open && (
        <Body>
          {/* Render reasoning and tool/code steps IN STREAM ORDER, so a tool the
              model called mid-thought shows WHERE it happened (reasoning → tool →
              reasoning) instead of every tool being lumped after all the text.
              Adjacent reasoning items coalesce into one paragraph block. */}
          {(() => {
            const out: React.ReactNode[] = [];
            let buf: string[] = [];
            const flushBuf = (k: number) => {
              const txt = buf.join('\n\n').trim();
              if (txt) out.push(<Draft key={`r-${k}`}>{txt}</Draft>);
              buf = [];
            };
            items.forEach((it: any, i) => {
              if (it.type === 'reasoning') {
                if (((it.reasoning || '') as string).trim()) buf.push(it.reasoning as string);
              } else if (it.type === 'tool_action') {
                flushBuf(i);
                out.push(
                  <StepRow key={i}>
                    <ToolActionBlock data={it as ToolActionContentItem} />
                  </StepRow>,
                );
              } else if (it.type === 'code_execution') {
                flushBuf(i);
                out.push(
                  <StepRow key={i}>
                    <CodeExecutionBlock data={it as CodeExecutionContentItem} />
                  </StepRow>,
                );
              } else if (it.type === 'search_results') {
                // Results ride along under the search that produced them —
                // inside the dropdown, not as a loose box below the group.
                flushBuf(i);
                out.push(
                  <StepRow key={i}>
                    <SearchResultsBlock data={it as SearchResultsContentItem} />
                  </StepRow>,
                );
              }
            });
            flushBuf(items.length);
            return out;
          })()}
        </Body>
      )}
    </Row>
  );
}

export default ThinkingGroup;

const Row = styled.div`
  ${activityRow}
`;

const Header = styled.div`
  ${activityHeader}
  max-width: 100%;
  /* Reclaim the assistant row's right-side padding for long live labels. The
     mobile cap stays strict so the header can never widen the viewport. */
  @media (min-width: 601px) {
    max-width: calc(100% + 20px);
  }
  min-width: 0;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  }
`;

const IconWrap = styled.span`
  ${activityIconSlot}
  opacity: 0.85;
`;

const Verb = styled.span<{ $pending?: boolean }>`
  min-width: 0;
  &[data-preserve-full-label=true] { flex-shrink: 0; }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  ${({ $pending }) => ($pending ? shimmerText : css``)}
`;

const Counter = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 9px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
`;

const Body = styled.div`
  /* Organized expansion: a subtle vertical guide ties the steps together as
     one timeline instead of a loose pile of boxes. */
  position: relative;
  margin: 2px 0 8px 22px;
  padding-left: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: 2px;
    border-radius: 1px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  }
`;

const StepRow = styled.div`
  position: relative;
  &::before {
    content: '';
    position: absolute;
    /* centro da bolinha (6px) alinhado ao centro da guia (2px em left:0 do
       Body, padding-left:14px): 14-16+3 = 1 = centro da linha */
    left: -16px;
    top: 13px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.22)'};
  }
`;

const Draft = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  opacity: 0.85;
  margin-bottom: 4px;
`;
